import React, { useEffect, useState, createContext, useContext } from 'react';
interface CurrencyInfo {
  code: string;
  symbol: string;
  rate: number;
}
interface CurrencyContextType {
  currency: CurrencyInfo;
  formatPrice: (usdAmount: number) => string;
  isLoading: boolean;
}
const CURRENCIES: Record<string, CurrencyInfo> = {
  USD: {
    code: 'USD',
    symbol: '$',
    rate: 1
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    rate: 0.92
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    rate: 0.79
  },
  MXN: {
    code: 'MXN',
    symbol: '$',
    rate: 17.2
  },
  COP: {
    code: 'COP',
    symbol: '$',
    rate: 4150
  },
  ARS: {
    code: 'ARS',
    symbol: '$',
    rate: 870
  },
  BRL: {
    code: 'BRL',
    symbol: 'R$',
    rate: 4.95
  },
  CLP: {
    code: 'CLP',
    symbol: '$',
    rate: 940
  },
  PEN: {
    code: 'PEN',
    symbol: 'S/',
    rate: 3.72
  },
  CAD: {
    code: 'CAD',
    symbol: 'CA$',
    rate: 1.36
  },
  AUD: {
    code: 'AUD',
    symbol: 'A$',
    rate: 1.53
  }
};
const TIMEZONE_CURRENCY_MAP: Record<string, string> = {
  'America/Mexico_City': 'MXN',
  'America/Cancun': 'MXN',
  'America/Monterrey': 'MXN',
  'America/Merida': 'MXN',
  'America/Chihuahua': 'MXN',
  'America/Mazatlan': 'MXN',
  'America/Hermosillo': 'MXN',
  'America/Tijuana': 'MXN',
  'America/Bogota': 'COP',
  'America/Buenos_Aires': 'ARS',
  'America/Argentina/Buenos_Aires': 'ARS',
  'America/Argentina/Cordoba': 'ARS',
  'America/Sao_Paulo': 'BRL',
  'America/Fortaleza': 'BRL',
  'America/Recife': 'BRL',
  'America/Santiago': 'CLP',
  'America/Lima': 'PEN',
  'America/Toronto': 'CAD',
  'America/Vancouver': 'CAD',
  'America/Edmonton': 'CAD',
  'America/Winnipeg': 'CAD',
  'Australia/Sydney': 'AUD',
  'Australia/Melbourne': 'AUD',
  'Australia/Brisbane': 'AUD',
  'Australia/Perth': 'AUD',
  'Europe/London': 'GBP',
  'Europe/Paris': 'EUR',
  'Europe/Berlin': 'EUR',
  'Europe/Madrid': 'EUR',
  'Europe/Rome': 'EUR',
  'Europe/Amsterdam': 'EUR',
  'Europe/Brussels': 'EUR',
  'Europe/Vienna': 'EUR',
  'Europe/Lisbon': 'EUR',
  'Europe/Dublin': 'EUR',
  'Europe/Helsinki': 'EUR',
  'Europe/Athens': 'EUR'
};
function detectCurrencyFromTimezone(): string {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Direct match
    if (TIMEZONE_CURRENCY_MAP[timezone]) {
      return TIMEZONE_CURRENCY_MAP[timezone];
    }
    // Region-based fallback
    if (timezone.startsWith('Europe/')) return 'EUR';
    if (timezone.startsWith('Australia/')) return 'AUD';
    return 'USD';
  } catch {
    return 'USD';
  }
}
const CurrencyContext = createContext<CurrencyContextType>({
  currency: CURRENCIES.USD,
  formatPrice: amount => `$${amount}`,
  isLoading: true
});
export function CurrencyProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [currency, setCurrency] = useState<CurrencyInfo>(CURRENCIES.USD);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const detected = detectCurrencyFromTimezone();
    setCurrency(CURRENCIES[detected] || CURRENCIES.USD);
    setIsLoading(false);
  }, []);
  const formatPrice = (usdAmount: number): string => {
    const converted = Math.round(usdAmount * currency.rate);
    // For large amounts (COP, ARS, CLP), format with locale separators
    if (converted >= 1000) {
      return `${currency.symbol}${converted.toLocaleString()}`;
    }
    return `${currency.symbol}${converted}`;
  };
  return <CurrencyContext.Provider value={{
    currency,
    formatPrice,
    isLoading
  }} data-id="element-630">
      {children}
    </CurrencyContext.Provider>;
}
export function useCurrency() {
  return useContext(CurrencyContext);
}